import { useMemo } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { OutlineSceneCard } from './OutlineSceneCard';
import type { OutlineNodeDto } from '../../api/queries/outlines';

interface OutlineCardViewProps {
  nodes: OutlineNodeDto[];
  selectedNodeId: string | null;
  searchQuery: string;
  onSelectNode: (id: string) => void;
}

export function OutlineCardView({
  nodes,
  selectedNodeId,
  searchQuery,
  onSelectNode,
}: OutlineCardViewProps) {
  const { groups, nodeMap } = useMemo(() => {
    const nodeMap = new Map(nodes.map((n) => [n.id, n]));
    // A node is a "leaf" if no other node has it as parentId
    const parentIds = new Set(nodes.map((n) => n.parentId).filter(Boolean));
    const leaves = nodes.filter((n) => !parentIds.has(n.id));

    const filtered = searchQuery
      ? leaves.filter(
          (n) =>
            n.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            n.synopsis?.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : leaves;

    const groups = new Map<string | null, OutlineNodeDto[]>();
    for (const n of filtered) {
      const key = n.parentId ?? null;
      (groups.get(key) ?? groups.set(key, []).get(key)!).push(n);
    }
    for (const arr of groups.values())
      arr.sort((a, b) => a.sequenceOrder - b.sequenceOrder);

    return { groups, nodeMap };
  }, [nodes, searchQuery]);

  return (
    <Box>
      {Array.from(groups.entries()).map(([parentId, items]) => {
        const parent = parentId ? nodeMap.get(parentId) : null;
        return (
          <Box key={parentId ?? 'root'} sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              {parent?.name ?? 'Top Level'}
            </Typography>
            <Grid container spacing={1.5}>
              {items.map((node) => (
                <Grid key={node.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <OutlineSceneCard
                    node={node}
                    isSelected={selectedNodeId === node.id}
                    onClick={() => onSelectNode(node.id)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      })}
    </Box>
  );
}
